// Reunderwriting Module (Tier 3) PR 3 — TanStack Query hooks for the
// case + per-case child resources. Mirrors the deal-flow @/hooks/deals
// pattern: every mutation invalidates the relevant list/detail keys so
// the pipeline + case-detail screens re-render without manual refresh.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  reunderwritingAssessmentsApi,
  reunderwritingAssignmentsApi,
  reunderwritingCasesApi,
  reunderwritingChecklistApi,
  reunderwritingEscalationsApi,
  reunderwritingFailuresApi,
  reunderwritingFailureTrackingApi,
  reunderwritingLookupsApi,
  reunderwritingMetricsApi,
} from "@/api/reunderwriting";
import { loanHealthKeys } from "@/hooks/portfolioAdmin/useLoanHealth";
import type {
  ArchiveReunderwritingCaseRequest,
  CriteriaLane,
  DeclineReunderwritingCaseRequest,
  IssueReunderwritingLaneAssessmentRequest,
  ResolveReunderwritingEscalationRequest,
  ReunderwritingApprovalRequest,
  ReunderwritingCaseListQuery,
  ReunderwritingEscalationFilter,
  ReunderwritingFailureFilter,
  ReunderwritingMetricsFilter,
  UpdateReunderwritingFailureRequest,
  UpdateReunderwritingJustificationRequest,
  UpsertReunderwritingAssignmentRequest,
  WithdrawReunderwritingCaseRequest,
} from "@/types/reunderwriting";

// ---- Query keys ----

export const reunderwritingKeys = {
  all: ["reunderwriting"] as const,
  lists: () => [...reunderwritingKeys.all, "case-list"] as const,
  list: (q: ReunderwritingCaseListQuery | undefined) =>
    [...reunderwritingKeys.lists(), q ?? {}] as const,
  detail: (id: string) => [...reunderwritingKeys.all, "case", id] as const,
  checklist: (caseId: string, lane: CriteriaLane) =>
    [...reunderwritingKeys.detail(caseId), "checklist", lane] as const,
  assessments: (caseId: string) =>
    [...reunderwritingKeys.detail(caseId), "assessments"] as const,
  outcome: (caseId: string) =>
    [...reunderwritingKeys.detail(caseId), "outcome"] as const,
  assignments: (caseId: string) =>
    [...reunderwritingKeys.detail(caseId), "assignments"] as const,
  failures: (caseId: string) =>
    [...reunderwritingKeys.detail(caseId), "failures"] as const,
  eventTypes: () => [...reunderwritingKeys.all, "event-types"] as const,
};

// ---- Case reads ----

export function useReunderwritingCases(query?: ReunderwritingCaseListQuery, enabled = true) {
  return useQuery({
    queryKey: reunderwritingKeys.list(query),
    queryFn: () => reunderwritingCasesApi.list(query),
    enabled,
  });
}

export function useReunderwritingCase(id: string | undefined) {
  return useQuery({
    queryKey: reunderwritingKeys.detail(id ?? ""),
    queryFn: () => reunderwritingCasesApi.get(id!),
    enabled: !!id,
  });
}

export function useReunderwritingEventTypes(enabled = true) {
  return useQuery({
    queryKey: reunderwritingKeys.eventTypes(),
    queryFn: () => reunderwritingLookupsApi.eventTypes(),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

// ---- Case lifecycle mutations ----

function useCaseInvalidate(caseId: string | undefined) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: reunderwritingKeys.lists() });
    if (caseId) qc.invalidateQueries({ queryKey: reunderwritingKeys.detail(caseId) });
  };
}

export function useDeclineReunderwritingCase(id: string) {
  const invalidate = useCaseInvalidate(id);
  return useMutation({
    mutationFn: (body: DeclineReunderwritingCaseRequest) =>
      reunderwritingCasesApi.decline(id, body),
    onSuccess: invalidate,
  });
}

export function useWithdrawReunderwritingCase(id: string) {
  const invalidate = useCaseInvalidate(id);
  return useMutation({
    mutationFn: (body: WithdrawReunderwritingCaseRequest) =>
      reunderwritingCasesApi.withdraw(id, body),
    onSuccess: invalidate,
  });
}

export function useArchiveReunderwritingCase(id: string) {
  const invalidate = useCaseInvalidate(id);
  return useMutation({
    mutationFn: (body: ArchiveReunderwritingCaseRequest) =>
      reunderwritingCasesApi.archive(id, body),
    onSuccess: invalidate,
  });
}

export function useRestoreReunderwritingCase(id: string) {
  const invalidate = useCaseInvalidate(id);
  return useMutation({
    mutationFn: () => reunderwritingCasesApi.restore(id),
    onSuccess: invalidate,
  });
}

export function useAdvanceToClosing(id: string) {
  const invalidate = useCaseInvalidate(id);
  return useMutation({
    mutationFn: () => reunderwritingCasesApi.advanceToClosing(id),
    onSuccess: invalidate,
  });
}

export function useApproveReunderwritingCase(id: string) {
  const qc = useQueryClient();
  const invalidate = useCaseInvalidate(id);
  return useMutation({
    mutationFn: (body: ReunderwritingApprovalRequest) =>
      reunderwritingCasesApi.approve(id, body),
    onSuccess: (result) => {
      invalidate();
      // Approval applies the downstream loan effect — refresh the subject
      // loan's health lights so the loan dashboard isn't stale.
      qc.invalidateQueries({
        queryKey: loanHealthKeys.health(result.subjectLoanId),
      });
    },
  });
}

// ---- Checklist ----

export function useReunderwritingChecklist(caseId: string | undefined, lane: CriteriaLane) {
  return useQuery({
    queryKey: reunderwritingKeys.checklist(caseId ?? "", lane),
    queryFn: () => reunderwritingChecklistApi.listByLane(caseId!, lane),
    enabled: !!caseId,
  });
}

function useChecklistInvalidate(caseId: string, lane: CriteriaLane) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: reunderwritingKeys.checklist(caseId, lane) });
    qc.invalidateQueries({ queryKey: reunderwritingKeys.failures(caseId) });
    qc.invalidateQueries({ queryKey: reunderwritingKeys.outcome(caseId) });
  };
}

export function useUpdateReunderwritingChecklistItem(caseId: string, lane: CriteriaLane) {
  const invalidate = useChecklistInvalidate(caseId, lane);
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: UpdateReunderwritingJustificationRequest }) =>
      reunderwritingChecklistApi.updateJustification(caseId, itemId, body),
    onSuccess: invalidate,
  });
}

export function useCertifyReunderwritingChecklistItem(caseId: string, lane: CriteriaLane) {
  const invalidate = useChecklistInvalidate(caseId, lane);
  return useMutation({
    mutationFn: (itemId: string) => reunderwritingChecklistApi.certify(caseId, itemId),
    onSuccess: invalidate,
  });
}

export function useDeclineReunderwritingChecklistItem(caseId: string, lane: CriteriaLane) {
  const invalidate = useChecklistInvalidate(caseId, lane);
  return useMutation({
    mutationFn: ({ itemId, failureReason }: { itemId: string; failureReason: string }) =>
      reunderwritingChecklistApi.decline(caseId, itemId, failureReason),
    onSuccess: invalidate,
  });
}

export function useEscalateReunderwritingChecklistItem(caseId: string, lane: CriteriaLane) {
  const invalidate = useChecklistInvalidate(caseId, lane);
  return useMutation({
    mutationFn: ({ itemId, escalationReason }: { itemId: string; escalationReason: string }) =>
      reunderwritingChecklistApi.escalate(caseId, itemId, escalationReason),
    onSuccess: invalidate,
  });
}

export function useWaiveReunderwritingChecklistItem(caseId: string, lane: CriteriaLane) {
  const invalidate = useChecklistInvalidate(caseId, lane);
  return useMutation({
    mutationFn: ({ itemId, rationale }: { itemId: string; rationale: string }) =>
      reunderwritingChecklistApi.waive(caseId, itemId, rationale),
    onSuccess: invalidate,
  });
}

// ---- Lane assessments + outcome ----

export function useReunderwritingAssessments(caseId: string | undefined) {
  return useQuery({
    queryKey: reunderwritingKeys.assessments(caseId ?? ""),
    queryFn: () => reunderwritingAssessmentsApi.list(caseId!),
    enabled: !!caseId,
  });
}

export function useReunderwritingOutcome(caseId: string | undefined) {
  return useQuery({
    queryKey: reunderwritingKeys.outcome(caseId ?? ""),
    queryFn: () => reunderwritingAssessmentsApi.outcome(caseId!),
    enabled: !!caseId,
  });
}

export function useIssueReunderwritingLaneAssessment(caseId: string, lane: CriteriaLane) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IssueReunderwritingLaneAssessmentRequest) =>
      reunderwritingAssessmentsApi.issue(caseId, lane, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reunderwritingKeys.assessments(caseId) });
      qc.invalidateQueries({ queryKey: reunderwritingKeys.outcome(caseId) });
    },
  });
}

export function useFinalizeReunderwritingOutcome(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reunderwritingAssessmentsApi.finalize(caseId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reunderwritingKeys.outcome(caseId) });
      qc.invalidateQueries({ queryKey: reunderwritingKeys.detail(caseId) });
    },
  });
}

// ---- Assignments ----

export function useReunderwritingAssignments(caseId: string | undefined) {
  return useQuery({
    queryKey: reunderwritingKeys.assignments(caseId ?? ""),
    queryFn: () => reunderwritingAssignmentsApi.list(caseId!),
    enabled: !!caseId,
  });
}

export function useUpsertReunderwritingAssignment(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleCode, body }: { roleCode: string; body: UpsertReunderwritingAssignmentRequest }) =>
      reunderwritingAssignmentsApi.upsert(caseId, roleCode, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reunderwritingKeys.assignments(caseId) });
      qc.invalidateQueries({ queryKey: reunderwritingKeys.detail(caseId) });
    },
  });
}

export function useMarkAssignmentNotApplicable(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleCode: string) =>
      reunderwritingAssignmentsApi.markNa(caseId, roleCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reunderwritingKeys.assignments(caseId) });
    },
  });
}

// ---- Failures (per case) ----

export function useReunderwritingCaseFailures(caseId: string | undefined) {
  return useQuery({
    queryKey: reunderwritingKeys.failures(caseId ?? ""),
    queryFn: () => reunderwritingFailuresApi.listByCase(caseId!),
    enabled: !!caseId,
  });
}

// ---- PR 4: page-level failures, escalations, and metrics hooks ----

export const reunderwritingPageKeys = {
  failures: (q: ReunderwritingFailureFilter | undefined) =>
    ["reunderwriting", "failures", "list", q ?? {}] as const,
  failureDetail: (id: string) =>
    ["reunderwriting", "failures", "detail", id] as const,
  escalations: (q: ReunderwritingEscalationFilter | undefined) =>
    ["reunderwriting", "escalations", "list", q ?? {}] as const,
  escalationDetail: (id: string) =>
    ["reunderwriting", "escalations", "detail", id] as const,
  metrics: (kind: string, q: ReunderwritingMetricsFilter | undefined) =>
    ["reunderwriting", "metrics", kind, q ?? {}] as const,
};

export function useReunderwritingFailures(query?: ReunderwritingFailureFilter) {
  return useQuery({
    queryKey: reunderwritingPageKeys.failures(query),
    queryFn: () => reunderwritingFailureTrackingApi.list(query),
  });
}

export function useReunderwritingFailure(id: string | undefined) {
  return useQuery({
    queryKey: reunderwritingPageKeys.failureDetail(id ?? ""),
    queryFn: () => reunderwritingFailuresApi.get(id!),
    enabled: !!id,
  });
}

export function useResolveReunderwritingFailure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { certifiedResolved: boolean; resolutionNotes: string };
    }) => reunderwritingFailuresApi.resolve(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reunderwriting", "failures"] });
    },
  });
}

export function useMarkReunderwritingFailureReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string | undefined }) =>
      reunderwritingFailuresApi.markReviewed(id, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reunderwriting", "failures"] });
    },
  });
}

export function useUpdateReunderwritingFailure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateReunderwritingFailureRequest }) =>
      reunderwritingFailureTrackingApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reunderwriting", "failures"] });
    },
  });
}

export function useReunderwritingEscalations(query?: ReunderwritingEscalationFilter) {
  return useQuery({
    queryKey: reunderwritingPageKeys.escalations(query),
    queryFn: () => reunderwritingEscalationsApi.list(query),
  });
}

export function useReunderwritingEscalation(id: string | undefined) {
  return useQuery({
    queryKey: reunderwritingPageKeys.escalationDetail(id ?? ""),
    queryFn: () => reunderwritingEscalationsApi.get(id!),
    enabled: !!id,
  });
}

export function useApproveReunderwritingEscalation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ResolveReunderwritingEscalationRequest }) =>
      reunderwritingEscalationsApi.approve(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reunderwriting", "escalations"] });
    },
  });
}

export function useDenyReunderwritingEscalation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ResolveReunderwritingEscalationRequest }) =>
      reunderwritingEscalationsApi.deny(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reunderwriting", "escalations"] });
    },
  });
}

const METRICS_STALE_60S = 60_000;

export function useReunderwritingPipelineMetrics(filter?: ReunderwritingMetricsFilter) {
  return useQuery({
    queryKey: reunderwritingPageKeys.metrics("pipeline", filter),
    queryFn: () => reunderwritingMetricsApi.pipeline(filter),
    staleTime: METRICS_STALE_60S,
  });
}

export function useReunderwritingQualityMetrics(filter?: ReunderwritingMetricsFilter) {
  return useQuery({
    queryKey: reunderwritingPageKeys.metrics("quality", filter),
    queryFn: () => reunderwritingMetricsApi.quality(filter),
    staleTime: METRICS_STALE_60S,
  });
}

export function useReunderwritingFailuresMetrics(filter?: ReunderwritingMetricsFilter) {
  return useQuery({
    queryKey: reunderwritingPageKeys.metrics("failures", filter),
    queryFn: () => reunderwritingMetricsApi.failures(filter),
    staleTime: METRICS_STALE_60S,
  });
}

export function useReunderwritingTimeToCompletionMetrics(filter?: ReunderwritingMetricsFilter) {
  return useQuery({
    queryKey: reunderwritingPageKeys.metrics("time-to-completion", filter),
    queryFn: () => reunderwritingMetricsApi.timeToCompletion(filter),
    staleTime: METRICS_STALE_60S,
  });
}
